import { useEffect, useRef, useState } from "react";
import { ActivityTimeline } from "../../components/ActivityTimeline";
import {
  BluetoothIcon,
  CheckIcon,
  InfoIcon,
  StopIcon
} from "../../components/Icons";
import { PresenceRail } from "../../components/PresenceRail";
import { readableError } from "../../lib/errors";
import { QiuiDiagnosticBridge } from "../../lib/qiuiBridge";
import type { ControlIntent, SessionState, User } from "../../types";

const safetyChecks = [
  "Collar is loose enough for two fingers",
  "Skin is dry and unbroken",
  "You have the physical reset key",
  "You are not alone or impaired"
];

interface DiagnosticReport {
  deviceName: string;
  connectedAt: number;
  connected: boolean;
  service: string;
  notify: string;
  write: string;
  notifications: string[];
}

export function WearerDashboard({
  user,
  state,
  relayConnected,
  lastIntent,
  send
}: {
  user: User;
  state: SessionState;
  relayConnected: boolean;
  lastIntent: ControlIntent | null;
  send: (message: Record<string, unknown>) => boolean;
}) {
  const bridge = useRef<QiuiDiagnosticBridge | null>(null);
  const [maxIntensity, setMaxIntensity] = useState(state.limits.maxIntensity);
  const [maxHoldSeconds, setMaxHoldSeconds] = useState(
    state.limits.maxHoldMs / 1_000
  );
  const [sessionMinutes, setSessionMinutes] = useState(30);
  const [checks, setChecks] = useState<boolean[]>(
    safetyChecks.map(() => false)
  );
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [deviceError, setDeviceError] = useState("");
  const [diagnostic, setDiagnostic] = useState<DiagnosticReport | null>(null);

  useEffect(() => {
    if (!lastIntent) return;
    if (
      lastIntent.intent === "start" &&
      lastIntent.hold &&
      state.device.mode === "simulator"
    ) {
      send({
        type: "bridge:ack",
        holdId: lastIntent.hold.id,
        status: "started"
      });
    }
  }, [lastIntent, send, state.device.mode]);

  useEffect(
    () => () => {
      bridge.current?.disconnect();
    },
    []
  );

  const allConfirmed = checks.every(Boolean);
  const canArm =
    relayConnected &&
    state.presence.controller &&
    state.device.outputReady &&
    allConfirmed;

  function updateCheck(index: number, value: boolean) {
    setChecks((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? value : item))
    );
  }

  function connectSimulator() {
    bridge.current?.disconnect();
    bridge.current = null;
    setDeviceError("");
    send({
      type: "device:update",
      device: { mode: "simulator", connected: true }
    });
  }

  async function connectDiagnostic() {
    setDeviceBusy(true);
    setDeviceError("");
    try {
      const nextBridge = new QiuiDiagnosticBridge();
      nextBridge.onNotification = (frame) =>
        setDiagnostic((current) =>
          current
            ? {
                ...current,
                notifications: [...current.notifications, frame].slice(-50)
              }
            : current
        );
      nextBridge.onDisconnect = () => {
        setDiagnostic((current) =>
          current ? { ...current, connected: false } : current
        );
        send({
          type: "device:update",
          device: { mode: "disconnected", connected: false }
        });
      };
      const details = await nextBridge.connect();
      bridge.current = nextBridge;
      setDiagnostic((current) => ({
        deviceName: details.name,
        connectedAt: Date.now(),
        connected: true,
        service: details.service,
        notify: details.notify,
        write: details.write,
        notifications: current?.notifications ?? []
      }));
      send({
        type: "device:update",
        device: { mode: "diagnostic", connected: true }
      });
    } catch (cause) {
      setDeviceError(readableError(cause));
    } finally {
      setDeviceBusy(false);
    }
  }

  function disconnect() {
    bridge.current?.disconnect();
    bridge.current = null;
    setDiagnostic((current) =>
      current ? { ...current, connected: false } : current
    );
    send({
      type: "device:update",
      device: { mode: "disconnected", connected: false }
    });
  }

  function arm() {
    send({
      type: "session:arm",
      maxIntensity,
      maxHoldMs: maxHoldSeconds * 1_000,
      sessionMs: sessionMinutes * 60_000,
      confirmed: allConfirmed
    });
  }

  function downloadDiagnosticReport() {
    if (!diagnostic) return;
    const payload = {
      generatedAt: new Date().toISOString(),
      app: "Velvet Signal",
      outputEnabled: false,
      ...diagnostic
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json"
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `velvet-signal-diagnostic-${new Date()
      .toISOString()
      .replaceAll(":", "-")}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const checklist = (
    <div className="checklist">
      {safetyChecks.map((label, index) => (
        <label key={label}>
          <input
            type="checkbox"
            checked={checks[index]}
            onChange={(event) => updateCheck(index, event.target.checked)}
            disabled={state.armed}
          />
          <CheckIcon />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="dashboard">
      <PresenceRail
        state={state}
        role={user.role}
        relayConnected={relayConnected}
      />
      <main className="wearer-main">
        <section className="wearer-workflow">
          <header className="page-heading page-heading--wearer">
            <h1>Your boundaries</h1>
            <p>You set the limits. You stay in control.</p>
          </header>

          <WorkflowStep number="1" title="Connect your collar">
            <div className="device-choice">
              <div>
                <span className={`status-dot ${state.device.connected ? "" : "status-dot--off"}`} />
                <strong>{state.device.connected ? state.device.name : "No bridge connected"}</strong>
                {state.device.connected && <small>{state.device.detail}</small>}
              </div>
              {state.device.mode === "hardware" ? (
                <span className="connected-color">
                  Managed by the Samsung background bridge
                </span>
              ) : state.device.connected ? (
                <button className="button button--outline" onClick={disconnect}>
                  Disconnect
                </button>
              ) : (
                <div className="button-row">
                  <button className="button button--outline" onClick={connectSimulator}>
                    Use safe simulator
                  </button>
                  <button
                    className="button button--outline"
                    onClick={connectDiagnostic}
                    disabled={deviceBusy || !QiuiDiagnosticBridge.isSupported()}
                  >
                    <BluetoothIcon />
                    {deviceBusy ? "Connecting…" : "Pair Little Devil"}
                  </button>
                </div>
              )}
            </div>
            {deviceError && <p className="form-error" role="alert">{deviceError}</p>}
            {state.device.mode === "diagnostic" && (
              <>
                <p className="inline-notice">
                  <InfoIcon />
                  BLE diagnostics are connected. Real output remains locked until
                  the device command transform is verified.
                </p>
                {diagnostic && (
                  <section className="diagnostic-readout" aria-live="polite">
                    <header>
                      <span>
                        <strong>Bench diagnostic</strong>
                        {diagnostic.deviceName}
                      </span>
                      <span className={diagnostic.connected ? "connected-color" : "warning-color"}>
                        {diagnostic.connected ? "CONNECTED" : "DISCONNECTED"}
                      </span>
                    </header>
                    <dl>
                      <div>
                        <dt>Verified service</dt>
                        <dd><code>{diagnostic.service}</code></dd>
                      </div>
                      <div>
                        <dt>Notification frames</dt>
                        <dd>{diagnostic.notifications.length}</dd>
                      </div>
                    </dl>
                    <div className="diagnostic-frames">
                      {diagnostic.notifications.length ? (
                        diagnostic.notifications.slice(-3).map((frame, index) => (
                          <code key={`${frame}-${index}`}>{frame || "(empty frame)"}</code>
                        ))
                      ) : (
                        <small>Waiting for non-output notifications…</small>
                      )}
                    </div>
                    <button
                      type="button"
                      className="button button--outline button--wide"
                      onClick={downloadDiagnosticReport}
                    >
                      DOWNLOAD DIAGNOSTIC REPORT
                    </button>
                  </section>
                )}
              </>
            )}
            {!QiuiDiagnosticBridge.isSupported() && !state.device.connected && (
              <p className="inline-notice">
                <InfoIcon />
                This browser does not provide Web Bluetooth. Open the wearer
                account in a Web Bluetooth-capable browser on the phone near the collar.
              </p>
            )}
          </WorkflowStep>

          <WorkflowStep number="2" title="Set this session’s limits">
            <div className="stepper-row">
              <Stepper
                label="Maximum intensity"
                value={maxIntensity}
                suffix="of 10"
                min={1}
                max={state.absoluteLimits.maxIntensity}
                onChange={setMaxIntensity}
                disabled={state.armed}
              />
              <Stepper
                label="Maximum hold"
                value={maxHoldSeconds}
                suffix="seconds"
                min={1}
                max={state.absoluteLimits.maxHoldMs / 1_000}
                onChange={setMaxHoldSeconds}
                disabled={state.armed}
              />
              <Stepper
                label="Session length"
                value={sessionMinutes}
                suffix="minutes"
                min={10}
                max={60}
                step={5}
                onChange={setSessionMinutes}
                disabled={state.armed}
              />
            </div>
          </WorkflowStep>

          <WorkflowStep number="3" title="Confirm control">
            <p>Controller access exists only while you keep this session armed.</p>
            <section className="before-arm before-arm--mobile">
              <h2>Before you arm</h2>
              {checklist}
              <p>If anything changes, stop and do not re-arm.</p>
            </section>
            <button
              className="button button--primary arm-button"
              onClick={arm}
              disabled={!canArm || state.armed}
            >
              {state.armed ? "SESSION ARMED" : "ARM SESSION"}
            </button>
            <small>
              {!state.device.outputReady
                ? state.device.mode === "hardware"
                  ? state.device.detail
                  : "Connect the safe simulator to test. Real output is still locked."
                : !state.presence.controller
                  ? "The controller must be present."
                  : !allConfirmed
                    ? "Confirm every safety check first."
                    : "A deliberate press arms the agreed limits."}
            </small>
          </WorkflowStep>
        </section>

        <aside className="before-arm before-arm--desktop">
          <h2>Before you arm</h2>
          {checklist}
          <p>If anything changes, stop and do not re-arm.</p>
        </aside>

        <ActivityTimeline events={state.events} compact />
      </main>

      <div className="safety-strip safety-strip--wearer">
        <div>
          <StopIcon />
          <span>Closing this page, disconnecting Bluetooth, or pressing Stop ends control.</span>
        </div>
        <button
          className="button button--danger"
          onClick={() => send({ type: "session:revoke" })}
        >
          <StopIcon /> STOP &amp; REVOKE
        </button>
      </div>
    </div>
  );
}

function WorkflowStep({
  number,
  title,
  children
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="workflow-step">
      <span className="workflow-step__number">{number}</span>
      <div>
        <h2>{title}</h2>
        {children}
      </div>
    </section>
  );
}

function Stepper({
  label,
  value,
  suffix,
  min,
  max,
  step = 1,
  disabled,
  onChange
}: {
  label: string;
  value: number;
  suffix: string;
  min: number;
  max: number;
  step?: number;
  disabled: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="stepper">
      <span>{label}</span>
      <div>
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={disabled || value <= min}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <strong>{value}</strong>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={disabled || value >= max}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
      <small>{suffix}</small>
    </div>
  );
}
